import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FUEL_RANGE_LABEL, formatFuelRange } from '../lib/fuelRange';
import { supabase } from '../lib/supabase';
import OperationPhotoGrid, {
  OPERATION_PHOTO_SLOTS,
  MAX_EXTRA_SCRATCHES,
  capturedSlotCount,
  missingSlotLabels,
  type SlotFiles,
} from '../components/OperationPhotoGrid';
import { FuelRangeInput } from '../components/shared/FuelRangeInput';
import StaffAvatar from '../components/shared/StaffAvatar';
import { Button } from '../components/media/MediaUI';
import { PickupPaymentSheet } from './bookings/PickupPaymentSheet';

// ─── Types ────────────────────────────────────────────────────────────────────

type OperationType =
  | 'DELIVERY' | 'PICKUP'
  | 'CAR_WASH' | 'MAINTENANCE' | 'OIL_CHANGE'
  | 'SERVICE'  | 'RECEIVING'   | 'OTHER'; // SERVICE/RECEIVING kept for legacy DB rows

type TabKey = 'dp' | 'other';

interface CarOption      { id: number; plate_number: string; model_group: { name: string } | null; }
interface ProfileOption  { id: string; full_name: string | null; role: string; }
interface CustomerOption { id: string; first_name: string; last_name: string; }

/** Shape of the `operations.photos` JSONB column for structured delivery/pickup rows. */
interface OperationPhotos {
  [key: string]: string | string[] | undefined;
  extra_scratches?: string[];
  base_path?: string;
}

interface HandlerProfile {
  id?: string | null;
  full_name: string | null;
  avatar_url: string | null;
  avatar_bg?: string | null;
  gender: string | null;
}

interface OperationRow {
  id: number;
  operation_date: string;
  operation_time: string | null;
  type: OperationType;
  car_id: number;
  performed_by: string | null;
  customer_id: string | null;
  current_km: number | null;
  fuel_level: number | null;
  cleanliness_status: string | null;
  location_text: string | null;
  note: string | null;
  booking_id: number | null;
  folder_url: string | null;
  photos: OperationPhotos | null;
  checklist_license_present: boolean | null;
  checklist_tutanak_present: boolean | null;
  checklist_air_freshener:   boolean | null;
  checklist_customer_card:   boolean | null;
  cars: { plate_number: string } | { plate_number: string }[] | null;
  handler: HandlerProfile | HandlerProfile[] | null;
  customers: { first_name: string; last_name: string } | { first_name: string; last_name: string }[] | null;
}

interface Operation {
  id: number;
  operation_date: string;
  operation_time: string | null;
  type: OperationType;
  car_id: number;
  performed_by: string | null;
  customer_id: string | null;
  plate_number: string;
  handler_name: string | null;
  handler_profile: HandlerProfile | null;
  customer_name: string | null;
  current_km: number | null;
  fuel_level: number | null;
  cleanliness_status: string | null;
  location_text: string | null;
  note: string | null;
  booking_id: number | null;
  folder_url: string | null;
  photos: OperationPhotos | null;
  checklist_license_present: boolean | null;
  checklist_tutanak_present: boolean | null;
  checklist_air_freshener:   boolean | null;
  checklist_customer_card:   boolean | null;
}

interface AddOpForm {
  type: OperationType;
  car_id: string;
  handled_by: string;
  customer_id: string;
  booking_id: string;
  operation_date: string;
  operation_time: string;
  current_km: string;
  fuel_level: string;
  cleanliness_status: 'clean' | 'not_clean' | '';
  location_text: string;
  note: string;
  // Delivery checklist. '' means unanswered — DELIVERY cannot submit until all
  // four are an explicit Yes or No, so there is deliberately no default.
  checklist_license_present: YesNo;
  checklist_tutanak_present: YesNo;
  checklist_air_freshener:   YesNo;
  checklist_customer_card:   YesNo;
}

type YesNo = 'yes' | 'no' | '';

type ChecklistKey =
  | 'checklist_license_present'
  | 'checklist_tutanak_present'
  | 'checklist_air_freshener'
  | 'checklist_customer_card';

// ─── Config ───────────────────────────────────────────────────────────────────

const TYPE_CONFIG: Record<OperationType, { label: string; color: string; bg: string; card: string }> = {
  DELIVERY:    { label: 'Delivery',    color: '#16a34a', bg: 'rgba(22,163,74,0.12)',    card: '#16a34a' },
  PICKUP:      { label: 'Pickup',      color: '#ef4444', bg: 'rgba(239,68,68,0.12)',    card: '#ef4444' },
  CAR_WASH:    { label: 'Car Wash',    color: '#0891b2', bg: 'rgba(8,145,178,0.12)',    card: '#0891b2' },
  MAINTENANCE: { label: 'Maintenance', color: '#6b7280', bg: 'rgba(107,114,128,0.12)', card: '#6b7280' },
  OIL_CHANGE:  { label: 'Oil Change',  color: '#ea580c', bg: 'rgba(234,88,12,0.12)',   card: '#ea580c' },
  SERVICE:     { label: 'Service',     color: '#7c3aed', bg: 'rgba(124,58,237,0.12)',  card: '#7c3aed' },
  RECEIVING:   { label: 'Receiving',   color: '#0f766e', bg: 'rgba(15,118,110,0.12)',  card: '#0f766e' },
  OTHER:       { label: 'Other',       color: '#9ca3af', bg: 'rgba(156,163,175,0.12)', card: '#9ca3af' },
};

const ALL_OP_TYPES: OperationType[] = [
  'DELIVERY', 'PICKUP', 'CAR_WASH', 'MAINTENANCE', 'OIL_CHANGE', 'OTHER',
];

const DP_TYPES:    OperationType[] = ['DELIVERY', 'PICKUP'];
const OTHER_TYPES: OperationType[] = ['CAR_WASH', 'MAINTENANCE', 'OIL_CHANGE', 'SERVICE', 'RECEIVING', 'OTHER']; // SERVICE/RECEIVING kept so legacy rows appear in the list

/** The delivery checklist, in display order. Labels are the source of truth for
 *  both the form and the validation message. */
const CHECKLIST_ITEMS: { key: ChecklistKey; label: string }[] = [
  { key: 'checklist_license_present', label: 'Vehicle license present?'       },
  { key: 'checklist_tutanak_present', label: 'Tutanak present?'               },
  { key: 'checklist_air_freshener',   label: 'Branded air freshener present?' },
  { key: 'checklist_customer_card',   label: 'Customer number card present?'  },
];

const DP_STAT_CARDS    = ['DELIVERY', 'PICKUP'] as const;
const OTHER_STAT_CARDS = ['CAR_WASH', 'MAINTENANCE', 'OIL_CHANGE', 'OTHER'] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getMonthStart(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), 1); }
function getMonthEnd(d: Date):   Date { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addMonths(d: Date, n: number): Date { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function formatMonthLabel(d: Date): string {
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}
function formatDate(s: string): string {
  if (!s) return '—';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
function todayStr(): string { return toDateStr(new Date()); }
function nowTimeStr(): string {
  const n = new Date();
  return `${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`;
}

function opToForm(op: Operation): AddOpForm {
  return {
    type:               op.type,
    car_id:             String(op.car_id),
    handled_by:         op.performed_by ?? '',
    customer_id:        op.customer_id ?? '',
    booking_id:         op.booking_id != null ? String(op.booking_id) : '',
    operation_date:     op.operation_date,
    operation_time:     op.operation_time ?? '',
    current_km:         op.current_km != null ? String(op.current_km) : '',
    fuel_level:         op.fuel_level != null ? String(op.fuel_level) : '',
    cleanliness_status: (op.cleanliness_status === 'clean' || op.cleanliness_status === 'not_clean')
                      ? op.cleanliness_status : '',
    location_text:      op.location_text ?? '',
    note:               op.note ?? '',
    checklist_license_present: boolToYesNo(op.checklist_license_present),
    checklist_tutanak_present: boolToYesNo(op.checklist_tutanak_present),
    checklist_air_freshener:   boolToYesNo(op.checklist_air_freshener),
    checklist_customer_card:   boolToYesNo(op.checklist_customer_card),
  };
}

function boolToYesNo(v: boolean | null | undefined): YesNo {
  return v === true ? 'yes' : v === false ? 'no' : '';
}

/** True when an operation carries any checklist answer at all. */
function hasChecklist(op: Operation): boolean {
  return CHECKLIST_ITEMS.some(item => op[item.key] != null);
}

function sanitizePath(s: string): string {
  return s
    .replace(/[^a-zA-Z0-9\-_]/g, '-')  // keep only alphanumeric, dash, underscore
    .replace(/-+/g, '-')                // collapse multiple dashes
    .replace(/^-|-$/g, '')             // trim leading/trailing dashes
    .toLowerCase();
}

// Storage folder key for a plate: lowercased, whitespace removed.
// Anything outside [a-z0-9-_] is dropped so the key stays storage-safe.
function plateFolderKey(plate: string): string {
  return plate.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9\-_]/g, '');
}

function resolveOperation(row: OperationRow): Operation {
  const car  = Array.isArray(row.cars)      ? row.cars[0]      : row.cars;
  const hdlr = Array.isArray(row.handler)   ? row.handler[0]   : row.handler;
  const cust = Array.isArray(row.customers) ? row.customers[0] : row.customers;
  return {
    id:                 row.id,
    operation_date:     row.operation_date,
    operation_time:     row.operation_time,
    type:               row.type,
    car_id:             row.car_id,
    performed_by:       row.performed_by,
    customer_id:        row.customer_id,
    plate_number:       car?.plate_number ?? '—',
    handler_name:       hdlr?.full_name ?? null,
    handler_profile:    hdlr ?? null,
    customer_name:      cust ? `${cust.first_name} ${cust.last_name}`.trim() : null,
    current_km:         row.current_km,
    fuel_level:         row.fuel_level,
    cleanliness_status: row.cleanliness_status,
    location_text:      row.location_text,
    note:               row.note,
    booking_id:         row.booking_id,
    folder_url:         row.folder_url,
    photos:             row.photos ?? null,
    checklist_license_present: row.checklist_license_present ?? null,
    checklist_tutanak_present: row.checklist_tutanak_present ?? null,
    checklist_air_freshener:   row.checklist_air_freshener   ?? null,
    checklist_customer_card:   row.checklist_customer_card   ?? null,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: number; bg: string; loading: boolean }> = ({
  label, value, bg, loading,
}) => (
  <div style={{ background: bg, borderRadius: 12, padding: '14px 18px', color: '#fff', display: 'flex', flexDirection: 'column', gap: 6 }}>
    <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.7px', opacity: 0.82 }}>{label}</div>
    <div style={{ fontSize: 34, fontWeight: 600, letterSpacing: '-1.5px', lineHeight: 1 }}>{loading ? '—' : value}</div>
  </div>
);

const MonthArrow: React.FC<{ direction: 'left' | 'right'; onClick: () => void }> = ({ direction, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${hovered ? '#4ba6ea' : '#e5e7eb'}`, background: hovered ? 'rgba(75,166,234,0.06)' : '#fff', color: hovered ? '#3A74C2' : '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 140ms ease', flexShrink: 0 }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        {direction === 'left'
          ? <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
          : <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>}
      </svg>
    </button>
  );
};

const TypeBadge: React.FC<{ type: OperationType }> = ({ type }) => {
  const cfg = TYPE_CONFIG[type] ?? TYPE_CONFIG.OTHER;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: cfg.color, background: cfg.bg, borderRadius: 20, padding: '3px 10px', whiteSpace: 'nowrap' }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
};

const SkeletonRow: React.FC<{ cols: number }> = ({ cols }) => (
  <tr>
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} style={{ padding: '9px 12px' }}>
        <div style={{ height: 13, width: [80, 110, 90, 110, 120, 70, 80, 120, 60][i] ?? 80, borderRadius: 6, background: '#f3f4f6', animation: 'pulse 1.5s ease-in-out infinite' }} />
      </td>
    ))}
  </tr>
);

const Th: React.FC<React.ThHTMLAttributes<HTMLTableCellElement>> = ({ children, style, ...rest }) => (
  <th style={{ padding: '9px 12px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.7px', textAlign: 'left', background: '#fff', borderBottom: '1.5px solid #f0f0f0', whiteSpace: 'nowrap', ...style }} {...rest}>
    {children}
  </th>
);

// ─── Toast ────────────────────────────────────────────────────────────────────

const Toast: React.FC<{ message: string; kind: ToastKind }> = ({ message, kind }) =>
  ReactDOM.createPortal(
    <div style={{
      position: 'fixed', bottom: 28, right: 28, zIndex: 2000,
      display: 'flex', alignItems: 'center', gap: 10,
      background: kind === 'success' ? '#0f1117' : kind === 'info' ? '#f3f4f6' : '#fff1f2',
      color:      kind === 'success' ? '#fff'    : kind === 'info' ? '#4b5563' : '#ef4444',
      border:     kind === 'error'   ? '1px solid #fecaca'
                : kind === 'info'    ? '1px solid #e5e7eb' : 'none',
      borderRadius: 12, padding: '12px 18px',
      fontSize: 13, fontWeight: 500,
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      animation: 'slideUp 200ms ease',
      maxWidth: 'min(460px, calc(100vw - 56px))',
    }}>
      {kind === 'success'
        ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10" fill="#16a34a"/><path d="M7 12l4 4 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        : kind === 'info'
        ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="9" stroke="#9ca3af" strokeWidth="1.8"/><path d="M12 11v5M12 8h.01" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/></svg>
        : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="1.8"/><path d="M12 8v5M12 16h.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/></svg>
      }
      {message}
    </div>,
    document.body,
  );

/** Yes / No segmented pair. Neither side is pre-selected — the caller passes ''
 *  until the user actively picks, which is what the DELIVERY validation checks. */
const YesNoToggle: React.FC<{
  value:    YesNo;
  disabled?: boolean;
  onChange: (v: 'yes' | 'no') => void;
}> = ({ value, disabled, onChange }) => (
  <div style={{ display: 'inline-flex', gap: 6, flexShrink: 0 }}>
    {(['yes', 'no'] as const).map(opt => {
      const active = value === opt;
      const tone   = opt === 'yes' ? '#16a34a' : '#ef4444';
      return (
        <button
          key={opt}
          type="button"
          disabled={disabled}
          aria-pressed={active}
          onClick={() => onChange(opt)}
          style={{
            minWidth: 64, minHeight: 44, padding: '0 14px',
            borderRadius: 9, fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 140ms ease',
            border:     active ? `1.5px solid ${tone}` : '1.5px solid #e5e7eb',
            color:      active ? tone : '#6b7280',
            background: active ? `${tone}14` : '#fff',
          }}
        >
          {opt === 'yes' ? 'Yes' : 'No'}
        </button>
      );
    })}
  </div>
);

/** Read-only Yes / No / — result, used on the operation view. */
const YesNoResult: React.FC<{ value: boolean | null }> = ({ value }) => {
  if (value == null) return <span style={{ fontSize: 13, color: '#d1d5db' }}>—</span>;
  const tone = value ? '#16a34a' : '#ef4444';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: tone }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
        {value
          ? <path d="M20 6L9 17l-5-5" stroke={tone} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
          : <path d="M18 6L6 18M6 6l12 12" stroke={tone} strokeWidth="2.4" strokeLinecap="round"/>}
      </svg>
      {value ? 'Yes' : 'No'}
    </span>
  );
};

// ─── Pickup charges (RPC-backed) ──────────────────────────────────────────────

type ToastKind = 'success' | 'error' | 'info';
interface ToastNotice { message: string; kind: ToastKind; settle?: PickupSettlement }
interface PickupSettlement { bookingId: number; charged: number }

/** Ledger descriptions written by `apply_pickup_charges` all start with this. */
const pickupChargeTag = (pickupId: number) => `pickup_charge:op=${pickupId}`;

/**
 * Verbatim shape of the jsonb returned by `preview_pickup_charges` and
 * `apply_pickup_charges`. Every figure is computed in Postgres — nothing here
 * is recalculated in the browser.
 */
interface PickupCharges {
  booking_id:   number | null;
  customer_id:  string | null;
  car_id:       number | null;
  rental_days:  number;
  km: {
    delivery_km: number; pickup_km: number; used: number;
    allowed: number; over: number; price_per_km: number; charge: number;
  };
  fuel: {
    delivery_level: number | null; pickup_level: number | null;
    drop: number; tolerance: number; liters: number;
    price_per_liter: number; charge: number;
  };
  wash: { delivery_clean: string; return_clean: string; charge: number };
  total_charge: number;
  applied?: boolean;
}

function formatTry(amount: number): string {
  return `${Number(amount).toLocaleString('en-US', { maximumFractionDigits: 2 })} ₺`;
}

/** "Extra km 250 ₺ + Fuel 180 ₺ = 430 ₺" — zero-value lines are left out. */
function chargeBreakdown(charges: PickupCharges): string {
  const parts: string[] = [];
  if (charges.km.charge   > 0) parts.push(`Extra km ${formatTry(charges.km.charge)}`);
  if (charges.fuel.charge > 0) parts.push(`Fuel ${formatTry(charges.fuel.charge)}`);
  if (charges.wash.charge > 0) parts.push(`Wash ${formatTry(charges.wash.charge)}`);
  return `${parts.join(' + ')} = ${formatTry(charges.total_charge)}`;
}

/**
 * Charges a finished pickup to the customer's wallet through
 * `apply_pickup_charges`, which does the arithmetic and the ledger inserts in
 * one transaction. Called exactly once, right after the pickup row is created —
 * never from the report modal, which only ever previews.
 *
 * Returns the toast to show. A failure here is reported but never rolls the
 * operation back: the pickup itself is already saved and the charges can be
 * applied later.
 */
async function applyPickupCharges(pickupId: number, uid: string | null): Promise<ToastNotice> {
  const { data, error } = await supabase.rpc('apply_pickup_charges', {
    p_pickup_operation_id: pickupId,
    p_created_by:          uid,
  });

  if (error) {
    // The function raises this when ledger rows already carry the pickup's tag,
    // which means someone already charged it — not something to alarm staff with.
    if (/already applied/i.test(error.message)) {
      return { message: 'Charges were already applied to this customer\u2019s wallet.', kind: 'info' };
    }
    return { message: `Operation saved, but charges failed: ${error.message}`, kind: 'error' };
  }

  const charges = data as PickupCharges | null;
  if (!charges || Number(charges.total_charge) <= 0) {
    return { message: 'Picked up — no extra charges.', kind: 'info' };
  }
  return {
    message: `Charged to customer wallet: ${chargeBreakdown(charges)}`,
    kind: 'success',
    settle: charges.booking_id === null ? undefined : { bookingId: charges.booking_id, charged: Number(charges.total_charge) },
  };
}

// ─── Add Operation Modal ──────────────────────────────────────────────────────

const EMPTY_FORM = (): AddOpForm => ({
  type:               'DELIVERY',
  car_id:             '',
  handled_by:         '',
  customer_id:        '',
  booking_id:         '',
  operation_date:     todayStr(),
  operation_time:     nowTimeStr(),
  current_km:         '',
  fuel_level:         '',
  cleanliness_status: '',
  location_text:      '',
  note:               '',
  checklist_license_present: '',
  checklist_tutanak_present: '',
  checklist_air_freshener:   '',
  checklist_customer_card:   '',
});

const AddOperationModal: React.FC<{
  onClose: () => void;
  /** Optional toast to show instead of the default "saved" one. */
  onSaved: (notice?: ToastNotice) => void;
  editOp?: Operation;
}> = ({ onClose, onSaved, editOp }) => {
  const isEdit = !!editOp;
  const [form, setForm]               = useState<AddOpForm>(isEdit ? opToForm(editOp!) : EMPTY_FORM);
  const [cars, setCars]               = useState<CarOption[]>([]);
  const [carsLoading, setCarsLoading] = useState(true);
  const [profiles, setProfiles]           = useState<ProfileOption[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(true);
  const [customers, setCustomers]         = useState<CustomerOption[]>([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [customersFallback, setCustomersFallback] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [photos, setPhotos]               = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [slotFiles, setSlotFiles]         = useState<SlotFiles>({});
  const [scratchFiles, setScratchFiles]   = useState<File[]>([]);
  const [uploadDone, setUploadDone]       = useState(0);
  const [uploadTotal, setUploadTotal]     = useState(0);
  const [saving, setSaving]           = useState(false);
  const [saveStep, setSaveStep]       = useState<'saving' | 'uploading'>('saving');
  const [formError, setFormError]     = useState<string | null>(null);
  const [fuelLevelError, setFuelLevelError] = useState<string | null>(null);

  // Refs to skip resets on initial mount when editing
  const skipCarResetRef     = useRef(isEdit);
  const skipBookingFillRef  = useRef(isEdit);

  // Fetch cars
  useEffect(() => {
    let active = true;
    supabase
      .from('cars')
      .select('id, plate_number, model_group:model_group_id(name)')
      .eq('is_active', true)
      .then(({ data }) => {
        if (active && data) {
          // Group by model, then plate within each model
          const sorted = (data as unknown as CarOption[]).sort((a, b) => {
            const nameA = a.model_group?.name ?? '';
            const nameB = b.model_group?.name ?? '';
            return nameA.localeCompare(nameB) || a.plate_number.localeCompare(b.plate_number);
          });
          setCars(sorted);
        }
        if (active) setCarsLoading(false);
      });
    return () => { active = false; };
  }, []);

  // Fetch staff/admin profiles
  useEffect(() => {
    let active = true;
    supabase
      .from('profiles')
      .select('id, full_name, role')
      .in('role', ['admin', 'staff'])
      .order('full_name')
      .then(({ data }) => {
        if (active && data) setProfiles(data as ProfileOption[]);
        if (active) setProfilesLoading(false);
      });
    return () => { active = false; };
  }, []);

  // Fetch customers filtered by the selected car's booking history
  useEffect(() => {
    if (!form.car_id) {
      setCustomers([]);
      setCustomersLoading(false);
      if (!skipCarResetRef.current) {
        setForm(f => ({ ...f, customer_id: '', booking_id: '' }));
      }
      return;
    }
    let active = true;
    setCustomersLoading(true);
    if (!skipCarResetRef.current) {
      setCustomers([]);
      setForm(f => ({ ...f, customer_id: '', booking_id: '' }));
    }
    (async () => {
      const { data } = await supabase
        .from('bookings')
        .select('customer_id, customers(id, first_name, last_name)')
        .eq('car_id', Number(form.car_id));
      if (!active) return;
      const seen = new Set<string>();
      const unique: CustomerOption[] = [];
      for (const row of (data ?? []) as Array<{ customer_id: string | null; customers: CustomerOption | CustomerOption[] | null }>) {
        const c = Array.isArray(row.customers) ? row.customers[0] : row.customers;
        if (c && !seen.has(c.id)) {
          seen.add(c.id);
          unique.push(c);
        }
      }
      unique.sort((a, b) => a.first_name.localeCompare(b.first_name));
      if (unique.length > 0) {
        setCustomers(unique);
        setCustomersFallback(false);
      } else {
        // Delivery / pickup cannot be saved without a linked customer, so a car
        // with no booking history would dead-end the operator. Fall back to the
        // whole customer list rather than leaving the dropdown empty.
        const { data: all } = await supabase
          .from('customers')
          .select('id, first_name, last_name')
          .order('first_name');
        if (!active) return;
        setCustomers((all ?? []) as CustomerOption[]);
        setCustomersFallback(true);
      }
      setCustomersLoading(false);
      skipCarResetRef.current = false;
    })();
    return () => { active = false; };
  }, [form.car_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear customer + booking when type leaves DELIVERY/PICKUP
  useEffect(() => {
    if (!(DP_TYPES as string[]).includes(form.type)) {
      setForm(f => ({ ...f, customer_id: '', booking_id: '' }));
    }
  }, [form.type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fill booking_id when customer is selected (skip on initial mount in edit mode)
  useEffect(() => {
    if (!form.customer_id || !(DP_TYPES as string[]).includes(form.type)) return;
    if (skipBookingFillRef.current) { skipBookingFillRef.current = false; return; }
    let active = true;
    setBookingLoading(true);
    supabase
      .from('bookings')
      .select('id')
      .eq('customer_id', form.customer_id)
      .order('created_at', { ascending: false })
      .limit(1)
      .single()
      .then(({ data }) => {
        if (!active) return;
        setBookingLoading(false);
        if (data) setForm(f => ({ ...f, booking_id: String(data.id) }));
      });
    return () => { active = false; };
  }, [form.customer_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Blob URL previews — revoke on change / unmount
  useEffect(() => {
    const urls = photos.map(f => URL.createObjectURL(f));
    setPreviewUrls(urls);
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [photos]);

  const set = (k: keyof AddOpForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  // Delivery / pickup in add mode use the fixed named-slot photo grid.
  // Every other type (and edit mode) keeps the free multi-photo uploader.
  const checklistAnswered = CHECKLIST_ITEMS.filter(item => form[item.key] !== '').length;

  // A delivery / pickup must end up linked to a real customer row: kabis builds
  // its name / id / nationality / licence columns from that link, and a name
  // typed into the note is not a link. A booking counts too — it carries the
  // customer itself. Historic rows saved before this rule stay editable, so
  // edit mode only forbids *clearing* a link that is already there.
  const isDpType         = (DP_TYPES as string[]).includes(form.type);
  const customerLinked   = !!form.customer_id || !!form.booking_id.trim();
  const customerRequired = isDpType && (!isEdit || editOp!.customer_id != null);
  const customerMissing  = customerRequired && !customerLinked;

  const usesPhotoSlots  = !isEdit && (DP_TYPES as string[]).includes(form.type);
  const capturedSlots   = capturedSlotCount(slotFiles);
  const missingSlots    = usesPhotoSlots ? missingSlotLabels(slotFiles) : [];
  const slotsIncomplete = usesPhotoSlots && missingSlots.length > 0;

  const handleSlotChange = useCallback((key: string, file: File) => {
    setSlotFiles(prev => ({ ...prev, [key]: file }));
  }, []);
  const handleScratchAdd = useCallback((file: File) => {
    setScratchFiles(prev => (prev.length >= MAX_EXTRA_SCRATCHES ? prev : [...prev, file]));
  }, []);
  const handleScratchRemove = useCallback((index: number) => {
    setScratchFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.car_id)     { setFormError('Please select a car.'); return; }
    if (!form.type)       { setFormError('Please select an operation type.'); return; }
    if (!form.handled_by) { setFormError('Please select who handled this operation.'); return; }
    if (customerMissing) {
      setFormError('Please select a linked customer for delivery/pickup operations.');
      return;
    }

    if (form.fuel_level === '') {
      setFuelLevelError('Fuel range is required.');
      return;
    }
    if (Number(form.fuel_level) > 2000) {
      setFuelLevelError('Maximum fuel range is 2,000 km');
      return;
    }
    // Every checklist item must be an explicit Yes or No before a NEW delivery
    // saves. Edit mode is exempt: existing deliveries predate the checklist, so
    // requiring it there would block unrelated edits on historic rows.
    if (!isEdit && form.type === 'DELIVERY') {
      const unanswered = CHECKLIST_ITEMS.filter(item => form[item.key] === '');
      if (unanswered.length > 0) {
        setFormError(
          `Delivery checklist incomplete — please answer Yes or No for: ${unanswered.map(i => i.label).join(', ')}`
        );
        return;
      }
    }

    if (usesPhotoSlots && missingSlots.length > 0) {
      setFormError(`${missingSlots.length} required photo(s) still missing: ${missingSlots.join(', ')}.`);
      return;
    }

    setSaving(true);
    setSaveStep('saving');
    setFormError(null);

    const corePayload: Record<string, unknown> = {
      type:               form.type,
      car_id:             Number(form.car_id),
      operation_date:     form.operation_date,
      operation_time:     form.operation_time || null,
      current_km:         form.current_km ? Number(form.current_km) : null,
      fuel_level:         form.fuel_level !== '' ? Number(form.fuel_level) : null,
      cleanliness_status: form.cleanliness_status || null,
      location_text:      form.location_text || null,
      note:               form.note || null,
      performed_by:       form.handled_by || null,
      customer_id:        form.customer_id || null,
      booking_id:         form.booking_id.trim() ? Number(form.booking_id) : null,
    };
    // Only DELIVERY carries a checklist; every other type explicitly nulls the
    // four columns so switching an operation's type never leaves stale answers.
    // Unanswered ('') stays null rather than collapsing to false — otherwise
    // editing a pre-checklist delivery would silently record four "No" answers.
    // New deliveries are validated above, so they always land as real booleans.
    for (const item of CHECKLIST_ITEMS) {
      const answer = form[item.key];
      corePayload[item.key] =
        form.type === 'DELIVERY' && answer !== '' ? answer === 'yes' : null;
    }


    // ── Edit mode ────────────────────────────────────────────────────────────
    if (isEdit) {
      const { error: updateError } = await supabase
        .from('operations')
        .update(corePayload)
        .eq('id', editOp!.id);
      setSaving(false);
      if (updateError) { setFormError(updateError.message); return; }
      onSaved();
      return;
    }

    // ── Add mode ─────────────────────────────────────────────────────────────
    const { data: { user } } = await supabase.auth.getUser();
    const uid = user?.id ?? null;

    const selectedCar      = cars.find(c => String(c.id) === form.car_id);
    const selectedCustomer = customers.find(c => c.id === form.customer_id);

    // ── Structured delivery / pickup photos ──────────────────────────────────
    // Upload every slot first; only insert once all of them succeeded, so a
    // failed upload can never leave a half-filled operation behind.
    if (usesPhotoSlots) {
      const plate  = plateFolderKey(selectedCar?.plate_number ?? '') || 'unknown';
      const opType = form.type.toLowerCase();
      const date   = form.operation_date.slice(0, 10);
      // Short unique suffix so two same-car/same-type/same-day operations never
      // overwrite each other's photos.
      const folderUid = (globalThis.crypto?.randomUUID?.() ?? String(Date.now())).slice(0, 8);
      const basePath  = `${plate}/${opType}-${date}/${folderUid}`;

      const targets: Array<{ path: string; file: File; slot: string }> = [
        ...OPERATION_PHOTO_SLOTS.flatMap(slot => {
          const file = slotFiles[slot.key];
          return file ? [{ path: `${basePath}/${slot.key}.jpg`, file, slot: slot.key }] : [];
        }),
        ...scratchFiles.map((file, i) => ({
          path: `${basePath}/scratch-${i + 1}.jpg`, file, slot: `scratch_${i + 1}`,
        })),
      ];

      setSaveStep('uploading');
      setUploadDone(0);
      setUploadTotal(targets.length);

      const uploaded: Record<string, string> = {};
      const stamp = Date.now();
      for (const target of targets) {
        const { error: uploadError } = await supabase.storage
          .from('operations')
          .upload(target.path, target.file, { upsert: true, contentType: target.file.type });

        if (uploadError) {
          setSaving(false);
          setUploadTotal(0);
          setFormError(`Photo upload failed (${target.slot}): ${uploadError.message}. Nothing was saved — please try again.`);
          return;
        }

        const { data: urlData } = supabase.storage.from('operations').getPublicUrl(target.path);
        // Fixed filenames + upsert mean the CDN may still hold the previous
        // image at this path, so stamp the URL to force a fresh fetch.
        uploaded[target.slot] = `${urlData.publicUrl}?t=${stamp}`;
        setUploadDone(d => d + 1);
      }

      const photosPayload: Record<string, unknown> = {};
      for (const slot of OPERATION_PHOTO_SLOTS) photosPayload[slot.key] = uploaded[slot.key];
      photosPayload.extra_scratches = scratchFiles.map((_, i) => uploaded[`scratch_${i + 1}`]);
      // Stored so future screens can locate these images without re-deriving the path.
      photosPayload.base_path = basePath;

      setSaveStep('saving');
      const { data: inserted, error: insertError } = await supabase
        .from('operations')
        .insert({ ...corePayload, created_by: uid, photos: photosPayload })
        .select('id')
        .single();

      if (insertError) {
        setSaving(false);
        setUploadTotal(0);
        setFormError(insertError.message);
        return;
      }

      // The one and only place charges are applied — tied to creating the
      // pickup, not to opening its report.
      const notice = form.type === 'PICKUP'
        ? await applyPickupCharges((inserted as { id: number }).id, uid)
        : undefined;

      setSaving(false);
      setUploadTotal(0);
      onSaved(notice);
      return;
    }

    // ── Legacy free-uploader path (all other types) ──────────────────────────
    const platePart    = sanitizePath(selectedCar?.plate_number ?? 'unknown');
    const typePart     = sanitizePath(form.type);
    const customerPart = selectedCustomer
      ? sanitizePath(`${selectedCustomer.first_name} ${selectedCustomer.last_name}`)
      : 'unknown';
    const folderName  = `${platePart}-${typePart}-${customerPart}`;
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL ?? '';
    const folderUrl   = photos.length > 0
      ? `${supabaseUrl}/storage/v1/object/public/operations/${folderName}/`
      : null;

    console.log('Uploading to folder:', folderName);

    const insertPayload = { ...corePayload, created_by: uid, folder_url: folderUrl };

    const { data: inserted, error: insertError } = await supabase
      .from('operations')
      .insert(insertPayload)
      .select('id')
      .single();
    if (insertError) { setSaving(false); setFormError(insertError.message); return; }

    // Edit mode returns earlier, so reaching here always means a new row. A
    // pickup only lands on this path if it somehow skips the slot uploader.
    const chargeNotice = form.type === 'PICKUP'
      ? await applyPickupCharges((inserted as { id: number }).id, uid)
      : undefined;

    // Upload photos if any
    if (photos.length > 0) {
      setSaveStep('uploading');
      let failCount = 0;
      for (const file of photos) {
        const ext      = file.name.includes('.') ? file.name.split('.').pop() : '';
        const baseName = sanitizePath(file.name.replace(/\.[^.]+$/, ''));
        const fileName = `${Date.now()}-${baseName}${ext ? `.${ext}` : ''}`;
        const path     = `${folderName}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('operations')
          .upload(path, file, { cacheControl: '3600', upsert: false });

        console.log('Upload result for', path, ':', uploadError ?? 'success');
        if (uploadError) failCount++;
      }
      setSaving(false);
      // A photo failure is the more urgent of the two messages, so it wins.
      if (failCount > 0) {
        onSaved({ message: `${failCount} photo(s) failed to upload. Operation was saved.`, kind: 'error' });
      } else {
        onSaved(chargeNotice);
      }
    } else {
      setSaving(false);
      onSaved(chargeNotice);
    }
  };

  // Shared input styles
  const inputStyle: React.CSSProperties = {
    width: '100%', height: 40, padding: '0 12px', fontSize: 13,
    border: '1.5px solid #e5e7eb', borderRadius: 9, outline: 'none',
    fontFamily: 'inherit', color: '#0f1117', background: '#fff',
    boxSizing: 'border-box', transition: 'border-color 140ms ease',
  };
  const selectStyle: React.CSSProperties = { ...inputStyle, cursor: 'pointer' };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 12, fontWeight: 600, color: '#374151',
    marginBottom: 6, letterSpacing: '0.1px',
  };
  const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column' };

  const onFocus = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    { (e.target as HTMLElement).style.borderColor = '#4ba6ea'; };
  const onBlur  = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    { (e.target as HTMLElement).style.borderColor = '#e5e7eb'; };

  return ReactDOM.createPortal(
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,17,23,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, animation: 'fadeIn 150ms ease' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 600, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.2)', animation: 'slideUp 180ms ease' }}
      >
        {/* Header */}
        <div style={{ padding: '24px 28px 18px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#0f1117', letterSpacing: '-0.3px' }}>{isEdit ? 'Edit Operation' : 'New Operation'}</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3 }}>{isEdit ? 'Update the operation details below' : 'Fill in the details below to log a new operation'}</div>
          </div>
          <button onClick={onClose}
            style={{ width: 34, height: 34, borderRadius: 9, border: 'none', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#e5e7eb'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6'; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#6b7280" strokeWidth="2.2" strokeLinecap="round"/></svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ overflowY: 'auto', flex: 1, padding: '24px 28px' }}>

          {/* Section: Operation Details */}
          <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 14 }}>
            Operation Details
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px', marginBottom: 16 }}>

            {/* Type */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Operation Type <span style={{ color: '#ef4444' }}>*</span></label>
              <select value={form.type} onChange={set('type')} onFocus={onFocus} onBlur={onBlur} style={selectStyle} required>
                {ALL_OP_TYPES.map(t => (
                  <option key={t} value={t}>{TYPE_CONFIG[t].label}</option>
                ))}
              </select>
            </div>

            {/* Car */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Car <span style={{ color: '#ef4444' }}>*</span></label>
              <select value={form.car_id} onChange={set('car_id')} onFocus={onFocus} onBlur={onBlur} style={selectStyle} required>
                <option value="">{carsLoading ? 'Loading cars…' : 'Select a car'}</option>
                {cars.map(c => (
                  <option key={c.id} value={String(c.id)}>
                    {c.plate_number}{c.model_group?.name ? ` — ${c.model_group.name}` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Handled By */}
            <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Handled By <span style={{ color: '#ef4444' }}>*</span></label>
              <select value={form.handled_by} onChange={set('handled_by')} onFocus={onFocus} onBlur={onBlur} style={selectStyle} required>
                <option value="">{profilesLoading ? 'Loading staff…' : 'Select a person'}</option>
                {profiles.map(p => (
                  <option key={p.id} value={p.id}>{p.full_name ?? p.id}</option>
                ))}
              </select>
            </div>

            {/* Customer + Booking ID — only for DELIVERY / PICKUP */}
            {(DP_TYPES as string[]).includes(form.type) && (
              <>
                <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>
                    Customer{' '}
                    {customerRequired
                      ? <span style={{ color: '#ef4444' }}>*</span>
                      : <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>}
                  </label>
                  <select
                    value={form.customer_id}
                    onChange={set('customer_id')}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    required={customerRequired}
                    disabled={!form.car_id || customersLoading}
                    style={{
                      ...selectStyle,
                      color: (!form.car_id || customersLoading) ? '#9ca3af' : '#0f1117',
                      opacity: 1,
                      borderColor: customerMissing ? '#fecaca' : selectStyle.borderColor,
                    }}
                  >
                    <option value="">
                      {!form.car_id
                        ? 'Select a car first'
                        : customersLoading
                          ? 'Loading customers…'
                          : customers.length === 0
                            ? 'No customers found'
                            : 'Select a customer'}
                    </option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                    ))}
                  </select>
                  {customerMissing && (
                    <span style={{ marginTop: 6, fontSize: 11.5, color: '#ef4444', lineHeight: 1.45 }}>
                      Required — pick the customer from the list. Writing the name in the note does not link them.
                    </span>
                  )}
                  {!customerMissing && customersFallback && !!form.car_id && !customersLoading && (
                    <span style={{ marginTop: 6, fontSize: 11.5, color: '#9ca3af', lineHeight: 1.45 }}>
                      No booking history for this car — showing all customers.
                    </span>
                  )}
                </div>

                <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>
                    Booking ID{' '}
                    <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
                    {bookingLoading && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ marginLeft: 6, animation: 'spin 0.7s linear infinite', verticalAlign: 'middle', color: '#3A74C2' }}>
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeDasharray="28 56"/>
                      </svg>
                    )}
                  </label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Auto-filled when a customer is selected"
                    value={form.booking_id}
                    readOnly
                    style={{ ...inputStyle, background: '#f3f4f6', color: form.booking_id ? '#374151' : '#9ca3af', cursor: 'not-allowed' }}
                  />
                </div>
              </>
            )}

            {/* Date */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Operation Date <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="date" value={form.operation_date} onChange={set('operation_date')} onFocus={onFocus} onBlur={onBlur} style={inputStyle} required />
            </div>

            {/* Time */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Operation Time</label>
              <input type="time" value={form.operation_time} onChange={set('operation_time')} onFocus={onFocus} onBlur={onBlur} style={inputStyle} />
            </div>

            {/* Mileage */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Mileage (km)</label>
              <input type="number" min="0" placeholder="e.g. 54200" value={form.current_km} onChange={set('current_km')} onFocus={onFocus} onBlur={onBlur} style={inputStyle} />
            </div>

            {/* Fuel range */}
            <div style={fieldStyle}>
              <label style={labelStyle}>{FUEL_RANGE_LABEL} <span style={{ color: '#ef4444' }}>*</span></label>
              <FuelRangeInput
                max="2000"
                value={form.fuel_level}
                onChange={value => {
                  setFuelLevelError(null);
                  setForm(f => ({ ...f, fuel_level: value }));
                }}
                onFocus={onFocus}
                onBlur={onBlur}
                style={{ ...inputStyle, borderColor: fuelLevelError ? '#ef4444' : undefined }}
              />
              {fuelLevelError && (
                <span style={{ fontSize: 12, color: '#ef4444', marginTop: 4, display: 'block' }}>{fuelLevelError}</span>
              )}
            </div>

            {/* Cleanliness */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Cleanliness Status</label>
              <select value={form.cleanliness_status} onChange={set('cleanliness_status')} onFocus={onFocus} onBlur={onBlur} style={selectStyle}>
                <option value="">Not specified</option>
                <option value="clean">✅ Clean</option>
                <option value="not_clean">❌ Not clean</option>
              </select>
            </div>

            {/* Location */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Location <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
              <input type="text" placeholder="e.g. Şişli branch" value={form.location_text} onChange={set('location_text')} onFocus={onFocus} onBlur={onBlur} style={inputStyle} />
            </div>
          </div>

          {/* Delivery checklist — DELIVERY only. Other types hide it and save nulls. */}
          {form.type === 'DELIVERY' && (
            <div style={{ marginBottom: 20, border: '1.5px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                padding: '10px 14px', background: 'var(--surface)', borderBottom: '1px solid var(--border-subtle)',
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ color: '#16a34a', flexShrink: 0 }}>
                  <path d="M9 11l3 3 8-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20 12v7a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Delivery Checklist <span style={{ color: '#ef4444' }}>*</span>
                </span>
                <span style={{
                  marginLeft: 'auto', fontSize: 11.5, fontWeight: 600,
                  color: checklistAnswered === CHECKLIST_ITEMS.length ? '#16a34a' : '#9ca3af',
                }}>
                  {checklistAnswered}/{CHECKLIST_ITEMS.length} answered
                </span>
              </div>
              {CHECKLIST_ITEMS.map((item, i) => (
                <div
                  key={item.key}
                  className="op-check-row"
                  style={{ borderTop: i === 0 ? 'none' : '1px solid #f5f5f5' }}
                >
                  <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{item.label}</span>
                  <YesNoToggle
                    value={form[item.key]}
                    disabled={saving}
                    onChange={v => setForm(f => ({ ...f, [item.key]: v }))}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Note */}
          <div style={{ ...fieldStyle, marginBottom: 20 }}>
            <label style={labelStyle}>Note <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
            <textarea
              rows={3}
              placeholder="Any additional notes…"
              value={form.note}
              onChange={set('note')}
              onFocus={onFocus}
              onBlur={onBlur}
              style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical', lineHeight: 1.5 }}
            />
          </div>

          {/* Photos — delivery / pickup use the fixed named-slot grid */}
          {usesPhotoSlots && (
            <OperationPhotoGrid
              slotFiles={slotFiles}
              onSlotChange={handleSlotChange}
              scratches={scratchFiles}
              onScratchAdd={handleScratchAdd}
              onScratchRemove={handleScratchRemove}
              disabled={saving}
            />
          )}

          {/* Photos — free uploader for every other type, add mode only */}
          {!isEdit && !usesPhotoSlots && <div style={fieldStyle}>
            <label style={labelStyle}>
              Photos <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
            </label>

            {/* Drop / click zone */}
            <label style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 6, padding: '18px 12px', border: '1.5px dashed #d1d5db', borderRadius: 10,
              cursor: 'pointer', background: '#fafafa', transition: 'border-color 140ms ease, background 140ms ease',
            }}
              onMouseEnter={e => { const l = e.currentTarget; l.style.borderColor = '#4ba6ea'; l.style.background = 'rgba(75,166,234,0.04)'; }}
              onMouseLeave={e => { const l = e.currentTarget; l.style.borderColor = '#d1d5db'; l.style.background = '#fafafa'; }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ color: '#9ca3af' }}>
                <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.6"/>
                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
                {photos.length > 0 ? `${photos.length} photo${photos.length > 1 ? 's' : ''} selected — click to add more` : 'Click to select photos'}
              </span>
              <span style={{ fontSize: 11, color: '#9ca3af' }}>JPG, PNG, WEBP accepted</span>
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={e => {
                  const incoming = Array.from(e.target.files ?? []);
                  if (incoming.length > 0) setPhotos(prev => [...prev, ...incoming]);
                  e.target.value = '';
                }}
              />
            </label>

            {/* Previews */}
            {previewUrls.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))', gap: 8, marginTop: 12 }}>
                {previewUrls.map((url, i) => (
                  <div key={i} style={{ position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', border: '1.5px solid #e5e7eb' }}>
                    <img src={url} alt={`Preview ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    <button
                      type="button"
                      onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
                      style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>}

          {formError && (
            <div style={{ marginTop: 16, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 9, fontSize: 13, color: '#ef4444' }}>
              {formError}
            </div>
          )}
        </form>

        {/* Footer */}
        <div style={{ padding: '16px 28px 24px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
          {customerMissing && !saving && (
            <div style={{ flex: '1 1 200px', minWidth: 0, fontSize: 11.5, color: '#9ca3af', lineHeight: 1.45 }}>
              <span style={{ fontWeight: 600, color: '#ef4444' }}>Customer required</span>{' '}
              — select a linked customer for delivery/pickup operations.
            </div>
          )}
          {slotsIncomplete && !saving && (
            <div style={{ flex: '1 1 200px', minWidth: 0, fontSize: 11.5, color: '#9ca3af', lineHeight: 1.45 }}>
              <span style={{ fontWeight: 600, color: '#ef4444' }}>
                {missingSlots.length} photo{missingSlots.length > 1 ? 's' : ''} still missing
              </span>{' '}
              ({capturedSlots} / {OPERATION_PHOTO_SLOTS.length} captured) — {missingSlots.join(', ')}
            </div>
          )}
          <button type="button" onClick={onClose}
            style={{ height: 40, padding: '0 20px', borderRadius: 10, border: '1px solid #EBEBEB', background: '#fff', fontSize: 13, fontWeight: 600, color: 'var(--foreground)', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 140ms ease' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#d1d5db'; (e.currentTarget as HTMLButtonElement).style.background = '#f9fafb'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#EBEBEB'; (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}>
            Cancel
          </button>
          <Button
            type="button"
            onClick={handleSubmit as unknown as React.MouseEventHandler}
            disabled={saving || slotsIncomplete || customerMissing}
            style={{ padding: '0 24px', fontSize: 13, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {saving ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 0.7s linear infinite' }}>
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeDasharray="28 56"/>
                </svg>
                {saveStep === 'uploading'
                  ? (uploadTotal > 0 ? `Uploading photos… ${uploadDone}/${uploadTotal}` : 'Uploading photos…')
                  : 'Saving…'}
              </>
            ) : isEdit ? 'Save Changes' : 'Save Operation'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ─── Confirm Delete Dialog ────────────────────────────────────────────────────

const ConfirmDeleteDialog: React.FC<{
  onConfirm: () => void;
  onCancel: () => void;
  deleting: boolean;
}> = ({ onConfirm, onCancel, deleting }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return ReactDOM.createPortal(
    <div
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(15,17,23,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, animation: 'fadeIn 150ms ease' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 400, padding: '28px 28px 24px', boxShadow: '0 24px 80px rgba(0,0,0,0.2)', animation: 'slideUp 180ms ease', textAlign: 'center' }}
      >
        <div style={{ width: 52, height: 52, borderRadius: 14, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M3 6h18M8 6V4h8v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10 11v5M14 11v5" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#0f1117', marginBottom: 8, letterSpacing: '-0.3px' }}>Delete Operation?</div>
        <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, marginBottom: 24 }}>
          This will permanently delete this operation and all associated photos.<br/>This action cannot be undone.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onCancel}
            disabled={deleting}
            style={{ flex: 1, height: 42, borderRadius: 10, border: '1px solid #EBEBEB', background: '#fff', fontSize: 14, fontWeight: 600, color: 'var(--foreground)', cursor: deleting ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all 140ms ease' }}
            onMouseEnter={e => { if (!deleting) { const b = e.currentTarget as HTMLButtonElement; b.style.background = '#f9fafb'; b.style.borderColor = '#d1d5db'; } }}
            onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = '#fff'; b.style.borderColor = '#EBEBEB'; }}
          >
            Cancel
          </button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={deleting}
            style={{ flex: 1, height: 42, fontSize: 14, fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}
          >
            {deleting ? (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 0.7s linear infinite' }}>
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeDasharray="28 56"/>
                </svg>
                Deleting…
              </>
            ) : 'Delete'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ─── Photos Modal ─────────────────────────────────────────────────────────────

/** One tile in the photos modal. `url` is null for a mandatory slot with no image. */
interface PhotoItem { url: string | null; label: string | null; }

const PhotosModal: React.FC<{
  folderUrl: string | null;
  structuredPhotos: OperationPhotos | null;
  /** Supplied so a DELIVERY's checklist result is visible after the fact. */
  operation?: Operation | null;
  onClose: () => void;
}> = ({ folderUrl, structuredPhotos, operation, onClose }) => {
  const [photos, setPhotos]     = useState<string[]>([]);
  // Structured rows resolve straight from the JSONB column — nothing to fetch.
  const [fetching, setFetching] = useState(!structuredPhotos);
  const [fetchErr, setFetchErr] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { if (lightbox) setLightbox(null); else onClose(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, lightbox]);

  useEffect(() => {
    if (structuredPhotos || !folderUrl) return;
    let active = true;
    const parts = folderUrl.split('/public/operations/');
    const folderPath = parts[1]?.replace(/\/$/, '') ?? '';

    supabase.storage
      .from('operations')
      .list(folderPath)
      .then(({ data, error }) => {
        if (!active) return;
        setFetching(false);
        if (error) { setFetchErr(error.message); return; }
        if (!data || data.length === 0) { setPhotos([]); return; }
        const urls = data
          .filter(f => f.name && !f.name.startsWith('.'))
          .map(f => supabase.storage.from('operations').getPublicUrl(`${folderPath}/${f.name}`).data.publicUrl);
        setPhotos(urls);
      });
    return () => { active = false; };
  }, [folderUrl, structuredPhotos]);

  // Structured rows: the 13 named positions in slot order, then the extra
  // scratches unlabeled. Legacy rows: whatever the folder listing returned.
  const items: PhotoItem[] = useMemo(() => {
    if (!structuredPhotos) return photos.map(url => ({ url, label: null }));

    const slots: PhotoItem[] = OPERATION_PHOTO_SLOTS.map(slot => {
      const value = structuredPhotos[slot.key];
      return { url: typeof value === 'string' && value ? value : null, label: slot.label };
    });
    const extras: PhotoItem[] = (Array.isArray(structuredPhotos.extra_scratches) ? structuredPhotos.extra_scratches : [])
      .filter((url): url is string => typeof url === 'string' && !!url)
      .map(url => ({ url, label: null }));

    return [...slots, ...extras];
  }, [structuredPhotos, photos]);

  const presentCount = items.filter(i => i.url).length;

  return ReactDOM.createPortal(
    <>
      {/* Main modal */}
      <div
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(15,17,23,0.6)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, animation: 'fadeIn 150ms ease' }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 680, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.22)', animation: 'slideUp 180ms ease' }}
        >
          {/* Header */}
          <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#0f1117', letterSpacing: '-0.3px' }}>Operation Photos</div>
              {!fetching && !fetchErr && (
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                  {presentCount} photo{presentCount !== 1 ? 's' : ''}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              style={{ width: 34, height: 34, borderRadius: 9, border: 'none', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#e5e7eb'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#6b7280" strokeWidth="2.2" strokeLinecap="round"/></svg>
            </button>
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
            {operation && operation.type === 'DELIVERY' && hasChecklist(operation) && (
              <div style={{ marginBottom: 20, border: '1.5px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                <div style={{
                  padding: '10px 14px', background: 'var(--surface)', borderBottom: '1px solid var(--border-subtle)',
                  fontSize: 12, fontWeight: 600, color: '#374151',
                  textTransform: 'uppercase', letterSpacing: '0.6px',
                }}>
                  Delivery Checklist
                </div>
                {CHECKLIST_ITEMS.map((item, i) => (
                  <div
                    key={item.key}
                    className="op-check-row"
                    style={{
                      borderTop: i === 0 ? 'none' : '1px solid #f5f5f5',
                      background: operation[item.key] === false ? 'rgba(239,68,68,0.04)' : 'transparent',
                    }}
                  >
                    <span style={{ fontSize: 13, color: '#374151' }}>{item.label}</span>
                    <YesNoResult value={operation[item.key]} />
                  </div>
                ))}
              </div>
            )}
            {fetching && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} style={{ aspectRatio: '4/3', borderRadius: 10, background: '#f3f4f6', animation: 'pulse 1.5s ease-in-out infinite' }} />
                ))}
              </div>
            )}

            {!fetching && fetchErr && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, fontSize: 13, color: '#ef4444' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="#ef4444" strokeWidth="1.8"/><path d="M12 8v4M12 16h.01" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round"/></svg>
                {fetchErr}
              </div>
            )}

            {!fetching && !fetchErr && presentCount === 0 && (
              <div style={{ textAlign: 'center', padding: '48px 0', color: '#9ca3af' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" style={{ margin: '0 auto 12px', display: 'block', color: '#d1d5db' }}>
                  <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5"/>
                  <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                  <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7280' }}>No photos found</div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>The folder exists but contains no photos</div>
              </div>
            )}

            {!fetching && !fetchErr && presentCount > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {items.map((item, i) => item.url === null ? (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ aspectRatio: '4/3', borderRadius: 10, border: '1.5px dashed #e5e7eb', background: 'var(--surface)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, color: '#d1d5db' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.6"/>
                        <path d="M4 20L20 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                      </svg>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af' }}>No photo</span>
                    </div>
                    {item.label && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textAlign: 'center' }}>{item.label}</span>
                    )}
                  </div>
                ) : (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <button
                      onClick={() => setLightbox(item.url as string)}
                      style={{ position: 'relative', aspectRatio: '4/3', borderRadius: 10, overflow: 'hidden', border: '1px solid #EBEBEB', cursor: 'zoom-in', padding: 0, background: '#fff', display: 'block', width: '100%', transition: 'border-color 140ms ease, transform 140ms ease' }}
                      onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = '#4ba6ea'; b.style.transform = 'scale(1.02)'; }}
                      onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = '#EBEBEB'; b.style.transform = 'scale(1)'; }}
                    >
                      <img
                        src={item.url}
                        alt={item.label ?? `Photo ${i + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', transition: 'background 140ms ease', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(0,0,0,0.12)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(0,0,0,0)'; }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ opacity: 0, transition: 'opacity 140ms ease', color: '#fff' }}>
                          <path d="M8 3H5a2 2 0 00-2 2v3M16 3h3a2 2 0 012 2v3M16 21h3a2 2 0 002-2v-3M8 21H5a2 2 0 01-2-2v-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                    </button>
                    {item.label && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#374151', textAlign: 'center' }}>{item.label}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, animation: 'fadeIn 120ms ease', cursor: 'zoom-out' }}
        >
          <img
            src={lightbox}
            alt="Full size"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: 10, boxShadow: '0 24px 80px rgba(0,0,0,0.6)', cursor: 'default' }}
          />
          <button
            onClick={() => setLightbox(null)}
            style={{ position: 'fixed', top: 20, right: 20, width: 40, height: 40, borderRadius: 10, border: 'none', background: 'rgba(255,255,255,0.12)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.22)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.12)'; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/></svg>
          </button>
          <a
            href={lightbox}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{ position: 'fixed', top: 20, right: 68, width: 40, height: 40, borderRadius: 10, border: 'none', background: 'rgba(255,255,255,0.12)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', textDecoration: 'none' }}
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.22)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.12)'; }}
            title="Open in new tab"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M15 3h6v6M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </a>
        </div>
      )}
    </>,
    document.body,
  );
};

// ─── Pickup Report Modal ──────────────────────────────────────────────────────

type MatchMethod = 'booking' | 'fallback';

interface DeliveryMatch {
  id: number;
  operation_date: string;
  operation_time: string | null;
  current_km: number | null;
  fuel_level: number | null;
  cleanliness_status: string | null;
  method: MatchMethod;
}

const DELIVERY_MATCH_COLUMNS = 'id, operation_date, operation_time, current_km, fuel_level, cleanliness_status';

const cleanlinessLabel = (s: string | null): string =>
  s === 'clean' ? 'Clean' : s === 'not_clean' ? 'Not clean' : '—';

/** Whole calendar days between two 'YYYY-MM-DD' dates (b − a). */
function daysBetween(a: string, b: string): number {
  const from = new Date(a + 'T00:00:00').getTime();
  const to   = new Date(b + 'T00:00:00').getTime();
  return Math.round((to - from) / 86400000);
}

function formatDuration(days: number): string {
  if (days === 0) return 'Same day';
  if (days === 1) return '1 day';
  return `${days} days`;
}

const MetricCard: React.FC<{
  label: string;
  value: React.ReactNode;
  raw: React.ReactNode;
  incomplete: boolean;
}> = ({ label, value, raw, incomplete }) => (
  <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '14px 16px', background: 'var(--surface)' }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 8 }}>
      {label}
    </div>
    <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.6px', lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>{raw}</div>
    {incomplete && (
      <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4, fontWeight: 600 }}>Incomplete data</div>
    )}
  </div>
);

/** One line of the charges breakdown. A zero amount is greyed rather than hidden,
 *  so staff can see the item was considered and came to nothing. */
const ChargeLine: React.FC<{ label: string; detail: string; amount: number }> = ({ label, detail, amount }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
    padding: '11px 16px', borderBottom: '1px solid #f0f0f0',
  }}>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: amount > 0 ? '#0f1117' : '#9ca3af' }}>{label}</div>
      <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 3, lineHeight: 1.45 }}>{detail}</div>
    </div>
    <span style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', color: amount > 0 ? '#ef4444' : '#d1d5db' }}>
      {formatTry(amount)}
    </span>
  </div>
);

const PickupReportModal: React.FC<{ pickup: Operation; onClose: () => void }> = ({ pickup, onClose }) => {
  const [delivery, setDelivery] = useState<DeliveryMatch | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  // Charges are read-only here. `preview_pickup_charges` never writes, and
  // `apply_pickup_charges` is deliberately not called from this modal — the
  // wallet is charged once, when the pickup is saved.
  const [charges,        setCharges]        = useState<PickupCharges | null>(null);
  const [chargesLoading, setChargesLoading] = useState(true);
  const [chargesError,   setChargesError]   = useState<string | null>(null);
  const [chargesApplied, setChargesApplied] = useState(false);

  useEffect(() => {
    let active = true;
    setChargesLoading(true);
    setChargesError(null);

    (async () => {
      const [previewRes, ledgerRes] = await Promise.all([
        supabase.rpc('preview_pickup_charges', { p_pickup_operation_id: pickup.id }),
        supabase
          .from('customer_accounting_ledger')
          .select('id', { count: 'exact', head: true })
          .like('description', `${pickupChargeTag(pickup.id)}%`),
      ]);

      if (!active) return;

      if (previewRes.error) {
        setChargesError(previewRes.error.message);
        setChargesLoading(false);
        return;
      }

      setCharges(previewRes.data as PickupCharges);
      setChargesApplied((ledgerRes.count ?? 0) > 0);
      setChargesLoading(false);
    })();

    return () => { active = false; };
  }, [pickup.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let active = true;

    const findDelivery = async (): Promise<void> => {
      // 1. Primary match — same booking_id
      if (pickup.booking_id != null) {
        const { data, error: bookingError } = await supabase
          .from('operations')
          .select(DELIVERY_MATCH_COLUMNS)
          .eq('type', 'DELIVERY')
          .eq('booking_id', pickup.booking_id)
          .order('operation_date', { ascending: false })
          .limit(1);

        if (!active) return;
        if (bookingError) { setError(bookingError.message); setLoading(false); return; }
        if (data && data.length > 0) {
          setDelivery({ ...(data[0] as Omit<DeliveryMatch, 'method'>), method: 'booking' });
          setLoading(false);
          return;
        }
      }

      // 2. Fallback match — latest delivery for the same car on/before the pickup date
      const { data, error: fallbackError } = await supabase
        .from('operations')
        .select(DELIVERY_MATCH_COLUMNS)
        .eq('type', 'DELIVERY')
        .eq('car_id', pickup.car_id)
        .lte('operation_date', pickup.operation_date)
        .order('operation_date', { ascending: false })
        .order('operation_time', { ascending: false, nullsFirst: false })
        .limit(1);

      if (!active) return;
      setLoading(false);
      if (fallbackError) { setError(fallbackError.message); return; }
      if (data && data.length > 0) {
        setDelivery({ ...(data[0] as Omit<DeliveryMatch, 'method'>), method: 'fallback' });
      }
    };

    findDelivery();
    return () => { active = false; };
  }, [pickup.booking_id, pickup.car_id, pickup.operation_date]);

  // ── Metrics ────────────────────────────────────────────────────────────────
  const kmKnown   = delivery != null && delivery.current_km != null && pickup.current_km != null;
  const kmUsed    = kmKnown ? (pickup.current_km as number) - (delivery!.current_km as number) : null;

  const rentalDays = delivery != null ? daysBetween(delivery.operation_date, pickup.operation_date) : null;

  const fuelKnown = delivery != null && delivery.fuel_level != null && pickup.fuel_level != null;
  const fuelDiff  = fuelKnown ? (pickup.fuel_level as number) - (delivery!.fuel_level as number) : null;

  const cleanKnown  = delivery != null && delivery.cleanliness_status != null && pickup.cleanliness_status != null;
  const washNeeded  = cleanKnown
    && delivery!.cleanliness_status === 'clean'
    && pickup.cleanliness_status === 'not_clean';

  const muted: React.CSSProperties = { color: '#d1d5db' };

  return ReactDOM.createPortal(
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1150, background: 'rgba(15,17,23,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, animation: 'fadeIn 150ms ease' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.2)', animation: 'slideUp 180ms ease' }}
      >
        {/* Header */}
        <div style={{ padding: '22px 26px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 600, color: '#0f1117', letterSpacing: '-0.3px' }}>
              Pickup Report — <span className="font-mono tabular-nums">{pickup.plate_number}</span>
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3 }}>
              Picked up on {formatDate(pickup.operation_date)}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ width: 34, height: 34, borderRadius: 9, border: 'none', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#e5e7eb'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#6b7280" strokeWidth="2.2" strokeLinecap="round"/></svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 26px 24px' }}>
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} style={{ height: 92, borderRadius: 12, background: '#f3f4f6', animation: 'pulse 1.5s ease-in-out infinite' }} />
              ))}
            </div>
          )}

          {!loading && error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, fontSize: 13, color: '#ef4444' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="#ef4444" strokeWidth="1.8"/><path d="M12 8v4M12 16h.01" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round"/></svg>
              {error}
            </div>
          )}

          {!loading && !error && !delivery && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7280', fontSize: 14 }}>
              No matching delivery found — cannot generate report.
            </div>
          )}

          {!loading && !error && delivery && (
            <>
              {/* Match summary */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
                <span style={{ fontSize: 13, color: '#374151' }}>
                  Matched delivery: <strong>{formatDate(delivery.operation_date)}</strong>
                </span>
                <span style={{
                  fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '4px 10px',
                  color:      delivery.method === 'booking' ? '#16a34a' : '#b45309',
                  background: delivery.method === 'booking' ? 'rgba(22,163,74,0.12)' : 'rgba(245,158,11,0.14)',
                }}>
                  {delivery.method === 'booking'
                    ? 'Matched by booking'
                    : 'Approximate — matched by last delivery for this car'}
                </span>
              </div>

              {/* Metrics */}
              <div style={{ display: 'grid', gap: 12 }}>
                <MetricCard
                  label="Rental duration"
                  incomplete={false}
                  value={rentalDays != null
                    ? <span style={{ color: '#0f1117' }}>{formatDuration(rentalDays)}</span>
                    : <span style={muted}>—</span>}
                  raw={`Delivery: ${formatDate(delivery.operation_date)} → Pickup: ${formatDate(pickup.operation_date)}`}
                />

                <MetricCard
                  label="Kilometers used"
                  incomplete={!kmKnown}
                  value={kmUsed != null
                    ? <span style={{ color: '#0f1117' }}>{Math.round(kmUsed).toLocaleString()} km</span>
                    : <span style={muted}>—</span>}
                  raw={kmKnown
                    ? `Delivery: ${(delivery.current_km as number).toLocaleString()} km → Pickup: ${(pickup.current_km as number).toLocaleString()} km`
                    : 'Mileage missing on delivery or pickup'}
                />

                <MetricCard
                  label="Fuel range difference"
                  incomplete={!fuelKnown}
                  value={fuelDiff != null
                    ? <span style={{ color: fuelDiff < 0 ? '#ef4444' : '#16a34a' }}>
                        {fuelDiff < 0 ? '−' : '+'}{formatFuelRange(Math.abs(fuelDiff))}
                      </span>
                    : <span style={muted}>—</span>}
                  raw={fuelKnown
                    ? `Delivery: ${formatFuelRange(delivery.fuel_level)} → Pickup: ${formatFuelRange(pickup.fuel_level)}`
                    : 'Fuel range missing on delivery or pickup'}
                />

                <MetricCard
                  label="Cleanliness"
                  incomplete={!cleanKnown}
                  value={cleanKnown
                    ? <span style={{
                        display: 'inline-block', fontSize: 12, fontWeight: 600, borderRadius: 20, padding: '5px 12px',
                        color:      washNeeded ? '#ef4444' : '#16a34a',
                        background: washNeeded ? 'rgba(239,68,68,0.12)' : 'rgba(22,163,74,0.12)',
                      }}>
                        {washNeeded ? 'Wash needed' : 'No wash needed'}
                      </span>
                    : <span style={muted}>—</span>}
                  raw={cleanKnown
                    ? `Delivered: ${cleanlinessLabel(delivery.cleanliness_status)} → Returned: ${cleanlinessLabel(pickup.cleanliness_status)}`
                    : 'Cleanliness status missing on delivery or pickup'}
                />
              </div>

              {/* Damage Inspection placeholder */}
              <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #f0f0f0' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 10 }}>
                  Damage Inspection
                </div>
                <div style={{ border: '1.5px dashed #e5e7eb', borderRadius: 12, padding: '26px 16px', textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>
                  Photo comparison coming soon
                </div>
              </div>
            </>
          )}

          {/* ── Charges due — server-computed, display only ── */}
          {!loading && (
            <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.7px' }}>
                  Charges Due
                </span>
                {chargesApplied && (
                  <span style={{
                    fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '4px 10px',
                    color: '#16a34a', background: 'rgba(22,163,74,0.12)',
                  }}>
                    ✓ Applied to wallet
                  </span>
                )}
              </div>

              {chargesLoading && (
                <div style={{ height: 88, borderRadius: 12, background: '#f3f4f6', animation: 'pulse 1.5s ease-in-out infinite' }} />
              )}

              {!chargesLoading && chargesError && (
                <div style={{ padding: '12px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, fontSize: 13, color: '#ef4444' }}>
                  {chargesError}
                </div>
              )}

              {!chargesLoading && !chargesError && charges && (
                <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 12, background: 'var(--surface)', overflow: 'hidden' }}>
                  <ChargeLine
                    label="Extra kilometers"
                    detail={`${Math.round(charges.km.used).toLocaleString()} km used of ${Math.round(charges.km.allowed).toLocaleString()} allowed`
                      + (charges.km.over > 0 ? ` · ${Math.round(charges.km.over).toLocaleString()} km over × ${formatTry(charges.km.price_per_km)}` : '')}
                    amount={charges.km.charge}
                  />
                  <ChargeLine
                    label="Fuel"
                    detail={charges.fuel.drop > 0
                      ? `Range down ${Math.round(charges.fuel.drop).toLocaleString()} km (tolerance ${Math.round(charges.fuel.tolerance).toLocaleString()} km) · ~${charges.fuel.liters} L to refill`
                      : 'Returned at or above the delivery range'}
                    amount={charges.fuel.charge}
                  />
                  <ChargeLine
                    label="Car wash"
                    detail={charges.wash.charge > 0
                      ? 'Delivered clean, returned dirty'
                      : 'No wash charge'}
                    amount={charges.wash.charge}
                  />
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                    padding: '13px 16px', background: '#fff', borderTop: '1px solid #f0f0f0',
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#0f1117' }}>Total</span>
                    <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-0.3px', color: charges.total_charge > 0 ? '#ef4444' : '#16a34a' }}>
                      {formatTry(charges.total_charge)}
                    </span>
                  </div>
                </div>
              )}

              {/* The RPC compares against the booking's delivery operation; with
                  none recorded it reads 0 km and every figure below is unusable. */}
              {!chargesLoading && !chargesError && charges && charges.km.delivery_km === 0 && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, padding: '10px 12px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                    <path d="M12 3l9 16H3l9-16z" stroke="#b45309" strokeWidth="1.8" strokeLinejoin="round"/>
                    <path d="M12 10v4M12 17h.01" stroke="#b45309" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                  <span style={{ fontSize: 12, color: '#b45309', lineHeight: 1.5 }}>
                    No reference delivery operation to compare against — these figures are incomplete.
                  </span>
                </div>
              )}

              {!chargesLoading && !chargesError && !chargesApplied && charges && charges.total_charge > 0 && (
                <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 10 }}>
                  Preview only — charges are posted to the wallet when the pickup is saved.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const OperationsPage: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('dp');

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [allOperations, setAllOperations] = useState<Operation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const [search, setSearch]         = useState('');
  const [typeFilter, setTypeFilter] = useState<OperationType | ''>('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editOp, setEditOp]         = useState<Operation | null>(null);
  const [deleteOp, setDeleteOp]     = useState<Operation | null>(null);
  const [deleting, setDeleting]     = useState(false);
  const [photosOp, setPhotosOp]     = useState<Operation | null>(null);
  const [reportOp, setReportOp]     = useState<Operation | null>(null);
  const [toast, setToast] = useState<ToastNotice | null>(null);
  const [settling, setSettling] = useState<PickupSettlement | null>(null);

  const showToast = (message: string, kind: ToastKind) => {
    setToast({ message, kind });
    setTimeout(() => setToast(null), 3500);
  };

  const handleTabChange = (t: TabKey) => {
    setTab(t);
    setTypeFilter('');
    setSearch('');
  };

  const handleDelete = async (op: Operation) => {
    setDeleting(true);

    // Structured delivery/pickup rows keep their photos under photos.base_path.
    // Cleanup failures are logged but must never block deleting the row itself.
    const basePath = typeof op.photos?.base_path === 'string' ? op.photos.base_path : null;

    if (basePath) {
      try {
        const { data: files, error: listError } = await supabase.storage.from('operations').list(basePath);
        if (listError) throw listError;
        if (files && files.length > 0) {
          const { error: removeError } = await supabase.storage
            .from('operations')
            .remove(files.map(f => `${basePath}/${f.name}`));
          if (removeError) throw removeError;
        }
      } catch (cleanupError) {
        console.error('[Op] failed to remove structured photos under', basePath, cleanupError);
      }
    }
    // Delete storage files if folder_url exists
    else if (op.folder_url) {
      const parts = op.folder_url.split('/public/operations/');
      const folderPath = parts[1]?.replace(/\/$/, '') ?? '';
      if (folderPath) {
        const { data: files } = await supabase.storage.from('operations').list(folderPath);
        if (files && files.length > 0) {
          await supabase.storage
            .from('operations')
            .remove(files.map(f => `${folderPath}/${f.name}`));
        }
      }
    }
    const { error } = await supabase.from('operations').delete().eq('id', op.id);
    setDeleting(false);
    setDeleteOp(null);
    if (error) {
      showToast('Failed to delete operation.', 'error');
    } else {
      fetchOperations(selectedMonth);
      showToast('Operation deleted.', 'success');
    }
  };

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchOperations = useCallback(async (month: Date) => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from('operations')
      .select(`
        id, operation_date, operation_time, type, car_id, performed_by, customer_id,
        current_km, fuel_level, cleanliness_status, location_text, note, booking_id, folder_url, photos,
        checklist_license_present, checklist_tutanak_present, checklist_air_freshener, checklist_customer_card,
        cars!operations_car_id_fkey(plate_number),
        handler:profiles!performed_by(id, full_name, avatar_url, avatar_bg, gender),
        customers(first_name, last_name)
      `)
      .gte('operation_date', toDateStr(getMonthStart(month)))
      .lte('operation_date', toDateStr(getMonthEnd(month)))
      .order('operation_date', { ascending: false });

    setLoading(false);
    if (fetchError) { setError(fetchError.message); return; }
    if (data && data.length > 0) console.log('[Op] first row cleanliness_status from DB:', (data[0] as Record<string, unknown>).cleanliness_status);
    setAllOperations(((data ?? []) as unknown as OperationRow[]).map(resolveOperation));
  }, []);

  useEffect(() => {
    fetchOperations(selectedMonth);
  }, [selectedMonth, fetchOperations]);

  // ── Split by tab ────────────────────────────────────────────────────────────
  const dpOps    = useMemo(() => allOperations.filter(op => (DP_TYPES    as string[]).includes(op.type)), [allOperations]);
  const otherOps = useMemo(() => allOperations.filter(op => (OTHER_TYPES as string[]).includes(op.type)), [allOperations]);

  const activeOps   = tab === 'dp' ? dpOps : otherOps;
  const activeTypes = tab === 'dp' ? DP_TYPES : OTHER_TYPES;

  // ── Stats ───────────────────────────────────────────────────────────────────
  const dpStats = useMemo(() => {
    const counts: Record<string, number> = { total: dpOps.length };
    DP_TYPES.forEach(t => { counts[t] = dpOps.filter(op => op.type === t).length; });
    return counts;
  }, [dpOps]);

  const otherStats = useMemo(() => {
    const counts: Record<string, number> = { total: otherOps.length };
    OTHER_TYPES.forEach(t => { counts[t] = otherOps.filter(op => op.type === t).length; });
    return counts;
  }, [otherOps]);

  const activeStats = tab === 'dp' ? dpStats : otherStats;

  // ── Filter ──────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let result = activeOps;
    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter(op =>
        op.plate_number.toLowerCase().includes(q) ||
        (op.customer_name ?? '').toLowerCase().includes(q) ||
        (op.handler_name  ?? '').toLowerCase().includes(q)
      );
    }
    if (typeFilter) result = result.filter(op => op.type === typeFilter);
    return result;
  }, [activeOps, search, typeFilter]);

  const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13, color: '#374151', verticalAlign: 'middle' };
  const colCount = tab === 'dp' ? 12 : 10;

  // ── Stat card definitions per tab ────────────────────────────────────────
  const dpStatCards = [
    { key: 'total', label: 'Total', bg: '#4ba6ea' },
    ...DP_STAT_CARDS.map(t => ({ key: t, label: TYPE_CONFIG[t].label, bg: TYPE_CONFIG[t].card })),
  ];
  const otherStatCards = [
    { key: 'total', label: 'Total', bg: '#4ba6ea' },
    ...OTHER_STAT_CARDS.map(t => ({ key: t, label: TYPE_CONFIG[t].label, bg: TYPE_CONFIG[t].card })),
  ];
  const activeStatCards = tab === 'dp' ? dpStatCards : otherStatCards;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', padding: '44px 40px' }}>

      {/* ── Page header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 28 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ba6ea' }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#3A74C2', letterSpacing: '0.8px', textTransform: 'uppercase' }}>Operations</span>
          </div>
          <h1 style={{ fontSize: 30, fontWeight: 500, letterSpacing: '-0.8px', color: '#0f1117', lineHeight: 1.1, marginBottom: 6 }}>Car Operations</h1>
          <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.5 }}>Deliveries, pickups, washes, maintenance and more</p>
        </div>

        {/* Temp: Storage test button */}
        <button onClick={async () => {
          const testFile = new File(['test content'], 'test.txt', { type: 'text/plain' });
          console.log('Testing upload to bucket: operations');
          const { data, error } = await supabase.storage
            .from('operations')
            .upload(`test-folder/test-${Date.now()}.txt`, testFile);
          console.log('Test upload result:', { data, error });
          alert(error ? `FAILED: ${error.message}` : `SUCCESS: ${data.path}`);
        }} style={{ height: 44, padding: '0 18px', borderRadius: 12, border: '1.5px dashed #e5e7eb', background: '#fff', fontSize: 13, fontWeight: 600, color: '#6b7280', cursor: 'pointer', fontFamily: 'inherit' }}>
          Test Storage Upload
        </button>

        {/* Add Operation button */}
        <Button
          onClick={() => setShowAddModal(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 44, padding: '0 22px', fontSize: 14, fontFamily: 'inherit', flexShrink: 0 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
          </svg>
          Add Operation
        </Button>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 28, background: '#f3f4f6', borderRadius: 12, padding: 4, alignSelf: 'flex-start', width: 'fit-content' }}>
        {([
          { key: 'dp',    label: 'Delivery & Pickup' },
          { key: 'other', label: 'Other Operations'  },
        ] as { key: TabKey; label: string }[]).map(t => (
          <button
            key={t.key}
            onClick={() => handleTabChange(t.key)}
            style={{
              padding: '8px 20px', borderRadius: 9, border: 'none',
              fontSize: 13, fontWeight: tab === t.key ? 600 : 500,
              color: tab === t.key ? '#0f1117' : '#6b7280',
              background: tab === t.key ? '#fff' : 'transparent',
              boxShadow: tab === t.key ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
              cursor: 'pointer', transition: 'all 160ms ease',
              fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Stat cards ── */}
      <div className="ops-stats" style={{ gridTemplateColumns: `repeat(${activeStatCards.length}, 1fr)` }}>
        {activeStatCards.map(c => (
          <StatCard key={c.key} label={c.label} value={activeStats[c.key] ?? 0} bg={c.bg} loading={loading} />
        ))}
      </div>

      {/* ── Month nav + search + filter ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <MonthArrow direction="left"  onClick={() => setSelectedMonth(m => addMonths(m, -1))} />
        <span style={{ fontSize: 15, fontWeight: 600, color: '#0f1117', minWidth: 160, textAlign: 'center' }}>
          {formatMonthLabel(selectedMonth)}
        </span>
        <MonthArrow direction="right" onClick={() => setSelectedMonth(m => addMonths(m, 1))} />

        <div style={{ flex: 1, minWidth: 0 }} />

        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value as OperationType | '')}
          style={{ height: 36, padding: '0 12px', fontSize: 13, border: '1.5px solid #e5e7eb', borderRadius: 9, outline: 'none', fontFamily: 'inherit', color: '#374151', background: '#fff', cursor: 'pointer' }}
        >
          <option value="">All types</option>
          {activeTypes.map(t => (
            <option key={t} value={t}>{TYPE_CONFIG[t].label}</option>
          ))}
        </select>

        <div style={{ position: 'relative' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }}>
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search plate, customer…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ height: 36, padding: '0 12px 0 32px', fontSize: 13, border: '1.5px solid #e5e7eb', borderRadius: 9, outline: 'none', fontFamily: 'inherit', color: '#0f1117', background: '#fff', width: 220, transition: 'border-color 140ms ease' }}
            onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#4ba6ea'; }}
            onBlur={e  => { (e.target as HTMLInputElement).style.borderColor = '#e5e7eb'; }}
          />
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ background: 'var(--surface)', borderRadius: 16, border: '1px solid var(--border-subtle)', boxShadow: '0 1px 6px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
        {error && (
          <div style={{ padding: '16px 20px', color: '#ef4444', fontSize: 13, borderBottom: '1px solid #fef2f2', background: '#fef2f2' }}>
            {error}
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: tab === 'dp' ? 980 : 860 }}>
            <thead>
              <tr>
                <Th>Date</Th>
                <Th>Type</Th>
                <Th>Plate</Th>
                <Th>Handled By</Th>
                {tab === 'dp' && <Th>Customer</Th>}
                <Th>Mileage</Th>
                <Th>Fuel range</Th>
                <Th style={{ textAlign: 'center' }}>Cleanliness</Th>
                <Th>Notes</Th>
                <Th style={{ textAlign: 'center' }}>Photos</Th>
                {tab === 'dp' && <Th style={{ textAlign: 'center' }}>Report</Th>}
                <Th style={{ textAlign: 'center' }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} cols={colCount} />)}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={colCount} style={{ padding: '60px 24px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                    {search || typeFilter ? 'No operations match your filters.' : 'No operations for this month.'}
                  </td>
                </tr>
              )}

              {!loading && filtered.map((op, idx) => (
                <tr
                  key={op.id}
                  style={{ borderTop: idx === 0 ? 'none' : '1px solid #f7f7f7', transition: 'background 100ms ease' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = '#f9fafb'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = ''; }}
                >
                  <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 500 }}>{formatDate(op.operation_date)}</td>

                  <td style={td}><TypeBadge type={op.type} /></td>

                  <td className="font-mono tabular-nums" style={{ ...td, fontSize: 12, fontWeight: 600 }}>{op.plate_number}</td>

                  <td style={{ ...td, color: op.handler_name ? '#374151' : '#d1d5db' }}>
                    {op.handler_name ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <StaffAvatar profile={op.handler_profile} size={24} />
                        {op.handler_name}
                      </span>
                    ) : '—'}
                  </td>

                  {tab === 'dp' && (
                    <td style={{ ...td, color: op.customer_name ? '#374151' : '#d1d5db' }}>
                      {op.customer_name ?? '—'}
                    </td>
                  )}

                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>
                    {op.current_km != null
                      ? op.current_km.toLocaleString() + ' km'
                      : <span style={{ color: '#d1d5db' }}>—</span>}
                  </td>

                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>
                    {op.fuel_level != null
                      ? formatFuelRange(op.fuel_level)
                      : <span style={{ color: '#d1d5db' }}>—</span>}
                  </td>

                  <td style={{ ...td, textAlign: 'center' }}>
                    {op.cleanliness_status === 'clean'
                      ? <span title="Clean" style={{ fontSize: 16 }}>✅</span>
                      : op.cleanliness_status === 'not_clean'
                        ? <span title="Not clean" style={{ fontSize: 16 }}>❌</span>
                        : <span style={{ color: '#d1d5db' }}>—</span>
                    }
                  </td>

                  <td style={{ ...td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: op.note ? '#374151' : '#d1d5db' }}>
                    {op.note ?? '—'}
                  </td>

                  <td style={{ ...td, textAlign: 'center' }}>
                    {(op.photos || op.folder_url || hasChecklist(op)) ? (
                      <button
                        onClick={() => setPhotosOp(op)}
                        title={op.photos || op.folder_url ? 'View photos' : 'View delivery checklist'}
                        style={{ width: 32, height: 32, borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#f9fafb', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#6b7280', transition: 'all 140ms ease' }}
                        onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = '#4ba6ea'; b.style.color = '#3A74C2'; b.style.background = 'rgba(75,166,234,0.07)'; }}
                        onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = '#e5e7eb'; b.style.color = '#6b7280'; b.style.background = '#f9fafb'; }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8"/>
                          <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                          <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    ) : (
                      <button
                        disabled
                        title="No photos"
                        style={{ width: 32, height: 32, borderRadius: 8, border: '1.5px solid #f0f0f0', background: '#fafafa', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'not-allowed', color: '#d1d5db' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8"/>
                          <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                          <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    )}
                  </td>

                  {/* Report — PICKUP rows only */}
                  {tab === 'dp' && (
                    <td style={{ ...td, textAlign: 'center' }}>
                      {op.type === 'PICKUP' ? (
                        <button
                          onClick={() => setReportOp(op)}
                          title="Pickup report"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid #EBEBEB', background: '#fff', cursor: 'pointer', color: 'var(--foreground)', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', transition: 'all 140ms ease' }}
                          onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = '#4ba6ea'; b.style.color = '#3A74C2'; b.style.background = 'rgba(75,166,234,0.07)'; }}
                          onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = '#EBEBEB'; b.style.color = 'var(--foreground)'; b.style.background = '#f9fafb'; }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                            <path d="M14 2v6h6M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Report
                        </button>
                      ) : (
                        <span style={{ color: '#d1d5db' }}>—</span>
                      )}
                    </td>
                  )}

                  {/* Edit + Delete */}
                  <td style={{ ...td, textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      {/* Edit */}
                      <button
                        onClick={() => setEditOp(op)}
                        title="Edit"
                        style={{ width: 32, height: 32, borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#f9fafb', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#6b7280', transition: 'all 140ms ease' }}
                        onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = '#4ba6ea'; b.style.color = '#3A74C2'; b.style.background = 'rgba(75,166,234,0.07)'; }}
                        onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = '#e5e7eb'; b.style.color = '#6b7280'; b.style.background = '#f9fafb'; }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      {/* Delete */}
                      <button
                        onClick={() => setDeleteOp(op)}
                        title="Delete"
                        style={{ width: 32, height: 32, borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#f9fafb', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#6b7280', transition: 'all 140ms ease' }}
                        onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = '#fca5a5'; b.style.color = '#ef4444'; b.style.background = 'rgba(239,68,68,0.07)'; }}
                        onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = '#e5e7eb'; b.style.color = '#6b7280'; b.style.background = '#f9fafb'; }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <path d="M3 6h18M8 6V4h8v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M10 11v5M14 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#9ca3af' }}>
              Showing <strong style={{ color: '#374151' }}>{filtered.length}</strong> of{' '}
              <strong style={{ color: '#374151' }}>{activeOps.length}</strong> operation{activeOps.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* ── Photos Modal ── */}
      {photosOp && (photosOp.photos || photosOp.folder_url || hasChecklist(photosOp)) && (
        <PhotosModal
          folderUrl={photosOp.folder_url}
          structuredPhotos={photosOp.photos}
          operation={photosOp}
          onClose={() => setPhotosOp(null)}
        />
      )}

      {/* ── Pickup Report Modal ── */}
      {reportOp && (
        <PickupReportModal pickup={reportOp} onClose={() => setReportOp(null)} />
      )}

      {/* ── Add Modal ── */}
      {showAddModal && (
        <AddOperationModal
          onClose={() => setShowAddModal(false)}
          onSaved={(notice) => {
            setShowAddModal(false);
            fetchOperations(selectedMonth);
            // A pickup reports what was charged; everything else just confirms.
            if (notice) {
              showToast(notice.message, notice.kind);
              setSettling(notice.settle ?? null);
            } else {
              showToast('Operation saved successfully.', 'success');
            }
          }}
        />
      )}

      {settling && (
        <PickupPaymentSheet
          bookingId={settling.bookingId}
          charged={settling.charged}
          onClose={() => setSettling(null)}
          onSaved={() => { setSettling(null); showToast('Payment recorded.', 'success'); }}
        />
      )}

      {/* ── Edit Modal ── */}
      {editOp && (
        <AddOperationModal
          editOp={editOp}
          onClose={() => setEditOp(null)}
          onSaved={() => {
            setEditOp(null);
            fetchOperations(selectedMonth);
            showToast('Operation updated successfully.', 'success');
          }}
        />
      )}

      {/* ── Delete Confirm ── */}
      {deleteOp && (
        <ConfirmDeleteDialog
          deleting={deleting}
          onCancel={() => setDeleteOp(null)}
          onConfirm={() => handleDelete(deleteOp)}
        />
      )}

      {/* ── Toast ── */}
      {toast && <Toast message={toast.message} kind={toast.kind} />}

      <style>{`
        .ops-stats {
          display: grid;
          gap: 12px;
          margin-bottom: 24px;
        }
        @media (max-width: 639px) {
          .ops-stats { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (min-width: 640px) and (max-width: 1023px) {
          .ops-stats { grid-template-columns: repeat(3, 1fr) !important; }
        }
        /* Delivery checklist rows — label left, Yes/No right; stacked on phones
           so the 44px targets never get squeezed. Global so the modal portals
           pick it up too. */
        .op-check-row {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; padding: 10px 14px; flex-wrap: wrap;
        }
        @media (max-width: 479px) {
          .op-check-row { flex-direction: column; align-items: flex-start; gap: 8px; }
        }
        @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:0.45} }
        @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes slideUp { from{transform:translateY(12px);opacity:0} to{transform:translateY(0);opacity:1} }
        @keyframes spin    { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
};

export default OperationsPage;
